import React, { useMemo } from 'react';
import { Route, Switch, useParams } from 'react-router-dom';

import { RouteWhenAuthenticated } from 'common/components';
import Loading from 'core/Loading';
import NotFound from 'core/NotFound';
import { ExperienceEntityType, ExperienceRouteParams } from 'modules/experience-core';

import { SpinnerContainer } from './Experience.styled';
import { BrandExperience } from './features/BrandExperience';
import { EnterpriseExperience } from './features/EnterpriseExperience';
import { MerchantExperience } from './features/MerchantExperience';
import { VenueExperience } from './features/VenueExperience';
import { useIsNewExperienceEnabled } from './hooks/useIsNewExperienceEnabled';
import { SearchPage } from './pages/SearchPage';
import { SupportPage } from './pages/SupportPage';

const isValidEntityType = (experienceEntityType: string): experienceEntityType is ExperienceEntityType => {
  return Object.values(ExperienceEntityType).includes(experienceEntityType as ExperienceEntityType);
};

type RequiredExperienceRouteParams = Required<ExperienceRouteParams>;

const EntityAwareExperience = ({ experienceEntityId, experienceEntityType }: RequiredExperienceRouteParams) => {
  const props = useMemo(
    () => ({ experienceEntityId, experienceEntityType }),
    [experienceEntityId, experienceEntityType],
  );
  return (
    <Switch>
      <RouteWhenAuthenticated path="/experience/venue/:experienceEntityId">
        <VenueExperience {...props} />
      </RouteWhenAuthenticated>
      <RouteWhenAuthenticated path="/experience/merchant/:experienceEntityId">
        <MerchantExperience {...props} />
      </RouteWhenAuthenticated>
      <RouteWhenAuthenticated path="/experience/brand/:experienceEntityId">
        <BrandExperience {...props} />
      </RouteWhenAuthenticated>
      <RouteWhenAuthenticated path="/experience/enterprise/:experienceEntityId">
        <EnterpriseExperience {...props} />
      </RouteWhenAuthenticated>
      <Route component={NotFound} />
    </Switch>
  );
};

const Experience = () => {
  const { experienceEntityId, experienceEntityType } = useParams<ExperienceRouteParams>();
  if (!experienceEntityType || !experienceEntityId || !isValidEntityType(experienceEntityType)) {
    return <NotFound />;
  }
  return <EntityAwareExperience experienceEntityId={experienceEntityId} experienceEntityType={experienceEntityType} />;
};

export const ExperienceEntry = () => {
  const { isLoading, isNewExperienceEnabled } = useIsNewExperienceEnabled();

  return (
    <>
      {isLoading ? (
        <SpinnerContainer>
          <Loading />
        </SpinnerContainer>
      ) : isNewExperienceEnabled ? (
        <Switch>
          <RouteWhenAuthenticated path="/experience/:experienceEntityType/:experienceEntityId">
            <Experience />
          </RouteWhenAuthenticated>
          <RouteWhenAuthenticated exact path="/experience/search">
            <SearchPage />
          </RouteWhenAuthenticated>
          <RouteWhenAuthenticated exact path="/experience/support">
            <SupportPage />
          </RouteWhenAuthenticated>
          <Route component={NotFound} />
        </Switch>
      ) : (
        <NotFound />
      )}
    </>
  );
};
